<template>
  <select
    :id="id"
    v-model="inputValue"
    class="tw-block tw-w-full tw-rounded tw-border tw-border-dorian-gray-100 tw-bg-white tw-text-dorian-gray-500 tw-outline-none tw-ring-offset-1 tw-transition-all"
    :class="[classes]"
    :name="name"
    :data-test-id="testId"
    :autofocus="autofocus"
    :disabled="disabled"
    @change="emitChange(inputValue)"
  >
    <cl-form-select-options-group
      v-if="isGroupType(options)"
      :options="options"
    />
    <cl-form-select-options v-if="isSingleType(options)" :options="options" />
  </select>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from "vue";
import type { Size } from "../../components/types";
import ClFormSelectOptions from "./cl-form-select-options.vue";
import ClFormSelectOptionsGroup from "./cl-form-select-options-group.vue";
import type { FormSelectOption, FormSelectOptionsGroup } from "./types";

type FormInputProps = {
  id: string;
  name: string;
  value: string;
  testId?: string;
  autofocus?: boolean;
  disabled?: boolean;
  options: FormSelectOption[] | FormSelectOptionsGroup[];
  size?: Size;
};

const props = withDefaults(defineProps<FormInputProps>(), {
  size: "medium",
  disabled: false,
  autofocus: false,
  testId: "",
});

const emit = defineEmits<{
  (event: "update:value", value: string | number): void;
  (event: "change", value: string | number): void;
}>();

const inputValue = ref(props.value);

const isGroupType = (
  options: FormSelectOptionsGroup[] | FormSelectOption[],
): options is FormSelectOptionsGroup[] =>
  Boolean(options.length && "options" in options[0] && "label" in options[0]);

const isSingleType = (
  options: FormSelectOptionsGroup[] | FormSelectOption[],
): options is FormSelectOption[] =>
  Boolean(options.length && "text" in options[0] && "value" in options[0]);

const sizes = {
  small: "tw-text-sm tw-py-1 tw-px-3",
  medium: "tw-text-base tw-py-2 tw-px-3",
  large: "tw-text-lg tw-py-3 tw-px-3",
};

const focusClasses =
  "focus:tw-border-secondary-500 focus:tw-ring-4 focus:tw-ring-secondary-500/10";

const disabledClasses =
  "disabled:tw-cursor-not-allowed disabled:tw-bg-dorian-gray-50 disabled:tw-text-dorian-gray-500/50 disabled:tw-outline-none";

const classes = computed(() => {
  return [sizes[props.size], focusClasses, disabledClasses];
});

watch(
  () => props.value,
  () => {
    inputValue.value = props.value;
  },
);

watch(inputValue, (newValue) => {
  emit("update:value", newValue);
});

const emitChange = (value: string | number) => {
  emit("change", value);
};
</script>

<style scoped></style>
